import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../lib/AuthContext";
import { db } from "../lib/firebase";
import {
  addDoc, collection, deleteDoc, doc, getDocs, query, serverTimestamp, where,
} from "firebase/firestore";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Car, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "../components/Spinner";
import { useCatalog } from "../lib/CatalogContext";

export function CarsList({ uid, allowAdd = true, allowDelete = true, readOnly = false, onChange, onAddSuccess }) {
  const { vehicleCategories } = useCatalog();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", registration: "", category: vehicleCategories[1]?.id || vehicleCategories[0]?.id || "" });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const q = query(collection(db, "cars"), where("uid", "==", uid));
      const snap = await getDocs(q);
      setCars(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [uid]);

  const handleAdd = async () => {
    if (!form.name.trim()) {
      toast.error("Vehicle name is required");
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, "cars"), {
        uid,
        name: form.name.trim(),
        registration: form.registration.trim().toUpperCase(),
        category: form.category,
        createdAt: serverTimestamp(),
      });
      toast.success("Vehicle added");
      setForm({ name: "", registration: "", category: vehicleCategories[1]?.id || vehicleCategories[0]?.id || "" });
      setOpen(false);
      await load();
      onChange?.();
      onAddSuccess?.();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteDoc(doc(db, "cars", id));
      toast.success("Vehicle removed");
      await load();
      onChange?.();
    } catch (err) { toast.error(err.message); }
    finally { setDeletingId(null); }
  };

  return (
    <div>
      <div className="flex justify-between items-baseline mb-4">
        <div className="font-mono-accent text-xs text-[#525252]">VEHICLES ({cars.length})</div>
        {allowAdd && !readOnly && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-car-button"
                size="sm"
                className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-mono-accent text-[10px] h-8 px-3"
              >
                <Plus size={12} className="mr-1" /> ADD VEHICLE
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md rounded-none border-[#D4D4D8]">
              <DialogHeader>
                <DialogTitle className="font-display font-black text-2xl tracking-tighter">Add Vehicle</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="font-mono-accent text-xs text-[#525252] block mb-2">VEHICLE NAME</label>
                  <Input
                    data-testid="car-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. My Swift, Dad's SUV…"
                    className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12"
                  />
                </div>
                <div>
                  <label className="font-mono-accent text-xs text-[#525252] block mb-2">CATEGORY</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="car-category" className="rounded-none border-[#D4D4D8]">
                      <SelectValue placeholder="Select type…" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {vehicleCategories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="font-mono-accent text-xs text-[#525252] block mb-2">REGISTRATION (OPTIONAL)</label>
                  <Input
                    data-testid="car-registration"
                    value={form.registration}
                    onChange={(e) => setForm({ ...form, registration: e.target.value.toUpperCase() })}
                    placeholder="KA-01-AB-1234"
                    style={{ textTransform: "uppercase" }}
                    className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12 font-mono"
                  />
                </div>
                <Button
                  data-testid="save-car-button"
                  onClick={handleAdd}
                  disabled={saving}
                  className="w-full bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs h-12"
                >
                  {saving ? <Spinner /> : "Save Vehicle"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#525252]">
          <Spinner /> Loading vehicles…
        </div>
      )}
      {!loading && cars.length === 0 && (
        <div className="border border-dashed border-[#D4D4D8] p-6 text-center">
          <Car size={24} className="mx-auto text-[#525252] mb-2" strokeWidth={1.5} />
          <div className="text-sm text-[#525252]">No vehicles added yet</div>
        </div>
      )}
      <div className="space-y-px bg-[#D4D4D8] border border-[#D4D4D8]">
        {cars.map((c) => {
          const cat = vehicleCategories.find((x) => x.id === c.category);
          return (
            <div key={c.id} data-testid={`car-row-${c.id}`} className="bg-white p-4 flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-lg">{c.name}</div>
                <div className="font-mono text-xs text-[#525252]">{c.registration || "no reg"}</div>
                <div className="font-mono-accent text-[10px] text-[#002FA7] mt-1">{cat?.short || c.category}</div>
              </div>
              {allowDelete && !readOnly && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      data-testid={`delete-car-${c.id}`}
                      disabled={deletingId === c.id}
                      className="text-[#FF2A2A] hover:bg-[#FF2A2A] hover:text-white p-2 transition-colors disabled:opacity-50"
                      aria-label="Delete vehicle"
                    >
                      {deletingId === c.id ? <Spinner /> : <Trash2 size={16} />}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="rounded-none border-[#D4D4D8]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display font-black text-2xl tracking-tighter">Remove vehicle?</AlertDialogTitle>
                      <AlertDialogDescription className="text-[#525252]">
                        Remove <span className="font-bold">{c.name}</span> from your profile? This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-none font-bold uppercase tracking-wide text-xs">Keep</AlertDialogCancel>
                      <AlertDialogAction
                        className="rounded-none bg-[#FF2A2A] hover:bg-[#CC0000] font-bold uppercase tracking-wide text-xs"
                        onClick={() => handleDelete(c.id)}
                      >
                        Remove
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, profile, updateName, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get("redirect");
  const [name, setName] = useState(profile?.name || "");
  const [saving, setSaving] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => { setName(profile?.name || ""); }, [profile]);

  const saveName = async () => {
    setSaving(true);
    try { await updateName(name); toast.success("Profile updated"); }
    catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setDeletingAccount(true);
    try {
      // Delete user's cars
      const carsQ = query(collection(db, "cars"), where("uid", "==", user.uid));
      const carsSnap = await getDocs(carsQ);
      await Promise.all(carsSnap.docs.map((d) => deleteDoc(doc(db, "cars", d.id))));
      // Delete user document
      await deleteDoc(doc(db, "users", user.uid));
      // Delete Firebase Auth account (also signs out automatically)
      await user.delete();
      await logout();
      navigate("/");
    } catch (err) {
      if (err.code === "auth/requires-recent-login") {
        toast.error("Please log out and log back in, then try again.");
      } else {
        toast.error("Failed to delete account: " + err.message);
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="font-mono-accent text-xs text-[#002FA7] mb-3">[ MY PROFILE ]</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-12">Your account.</h1>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="font-mono-accent text-xs text-[#525252] mb-4">PERSONAL</div>
              <div className="space-y-6">
                <div>
                  <label className="font-mono-accent text-xs text-[#525252] block mb-2">NAME</label>
                  <Input
                    data-testid="profile-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12"
                  />
                </div>
                <div>
                  <label className="font-mono-accent text-xs text-[#525252] block mb-2">PHONE</label>
                  <div className="text-lg font-mono h-12 flex items-center border-b-2 border-[#D4D4D8]">
                    {user.phoneNumber || profile?.phone || "—"}
                  </div>
                </div>
                <Button
                  data-testid="profile-save-button"
                  onClick={saveName}
                  disabled={saving || !name.trim()}
                  className="bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wide text-xs h-12 px-8"
                >
                  {saving ? <Spinner /> : "Save"}
                </Button>
              </div>
            </div>

            <div>
              <CarsList
                uid={user.uid}
                onAddSuccess={redirectTo ? () => navigate(redirectTo) : undefined}
              />
            </div>
          </div>

          {/* DANGER ZONE */}
          <div className="mt-20 pt-10 border-t border-[#D4D4D8]">
            <div className="font-mono-accent text-xs text-[#FF2A2A] mb-3">DANGER ZONE</div>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="font-display font-bold text-lg">Delete account</div>
                <p className="text-sm text-[#525252]">Permanently remove your account and all vehicle data.</p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-none border-2 border-[#FF2A2A] text-[#FF2A2A] hover:bg-[#FF2A2A] hover:text-white font-bold uppercase tracking-wide text-xs h-12 px-6"
                  >
                    Delete Account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none border-[#D4D4D8]">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-display font-black text-2xl tracking-tighter">Delete your account?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[#525252]">
                      This will permanently delete your account and all your vehicles. Your booking history will be retained for store records. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-none font-bold uppercase tracking-wide text-xs">Keep account</AlertDialogCancel>
                    <AlertDialogAction
                      disabled={deletingAccount}
                      className="rounded-none bg-[#FF2A2A] hover:bg-[#CC0000] font-bold uppercase tracking-wide text-xs"
                      onClick={handleDeleteAccount}
                    >
                      {deletingAccount ? <Spinner /> : "Yes, delete"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
